// Snapshot of the live App.js — restore its contents into App.js to bring Geneo AI back online.
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { DevModeProvider, useDevMode } from "@/context/DevModeContext";
import Studio from "@/pages/Studio";
import Gallery from "@/pages/Gallery";
import Pricing from "@/pages/Pricing";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import AuthCallback from "@/pages/AuthCallback";
import Storyboard from "@/pages/Storyboard";
import TimelineEditor from "@/pages/TimelineEditor";
import ResetPassword from "@/pages/ResetPassword";
import PublicProfile from "@/pages/PublicProfile";
import InsufficientCreditsModal from "@/components/InsufficientCreditsModal";
import SignInOverlay from "@/components/SignInOverlay";
import FeedbackChip from "@/components/FeedbackChip";
import NovaChat from "@/components/NovaChat";
import AdminFeedback from "@/pages/AdminFeedback";
import AvatarStudio from "@/pages/AvatarStudio";
import QRGenerator from "@/pages/QRGenerator";
import QRRedirect from "@/pages/QRRedirect";
import MyQRCodes from "@/pages/MyQRCodes";
import Profile from "@/pages/Profile";
import DevPanel from "@/components/dev/DevPanel";
import { Sparkles } from "lucide-react";
import "@/App.css";

// Studio wrapper — always renders Studio; overlays a sign-in modal when the user is not authenticated.
const StudioGuarded = () => {
  const { user, loading } = useAuth();
  return (
    <>
      <Studio />
      {!loading && !user && <SignInOverlay />}
    </>
  );
};

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A]">
        <Sparkles className="w-8 h-8 text-[#F2C94C] animate-pulse" />
      </div>
    );
  }
  if (!user) return <Navigate to="/" replace />;
  return children;
};

/**
 * FeatureRoute — gate a route on a global feature flag. Admin bypass allowed
 * (they always see everything) so operators can preview flagged-off tools.
 * Non-admins with a disabled flag redirect to /studio.
 */
const FeatureRoute = ({ flag, children }) => {
  const { user, loading } = useAuth();
  const { isFeatureEnabled, flagsLoading } = useDevMode();
  if (loading || flagsLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A]">
        <Sparkles className="w-8 h-8 text-[#F2C94C] animate-pulse" />
      </div>
    );
  }
  if (!user) return <Navigate to="/" replace />;
  if (!isFeatureEnabled(flag)) return <Navigate to="/studio" replace />;
  return children;
};

function AppRouter() {
  const location = useLocation();
  // Capture referral code from ?ref=XXX and stash for use in AuthCallback after signup
  if (typeof window !== "undefined") {
    const params = new URLSearchParams(location.search);
    const ref = params.get("ref");
    if (ref) {
      try { localStorage.setItem("geneo_ref", ref); } catch {}
    }
  }
  // Handle OAuth callback — session_id can arrive as hash fragment (`#session_id=...`)
  // OR as a query string (`?session_id=...`). Cover both so the redirect never gets stuck.
  const hasSessionInHash = location.hash?.includes("session_id=");
  const hasSessionInQuery = location.search?.includes("session_id=");
  if (hasSessionInHash || hasSessionInQuery) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<StudioGuarded />} />
      <Route path="/studio" element={<StudioGuarded />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/payment/cancel" element={<PaymentCancel />} />
      <Route path="/gallery" element={<FeatureRoute flag="gallery"><Gallery /></FeatureRoute>} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/u/:slug" element={<PublicProfile />} />
      <Route path="/storyboard" element={<FeatureRoute flag="storyboard"><Storyboard /></FeatureRoute>} />
      <Route path="/editor" element={<FeatureRoute flag="timeline_editor"><TimelineEditor /></FeatureRoute>} />
      <Route path="/avatar" element={<FeatureRoute flag="avatar_studio"><AvatarStudio /></FeatureRoute>} />
      <Route path="/admin/feedback" element={<ProtectedRoute><AdminFeedback /></ProtectedRoute>} />
      <Route path="/qr-generator" element={<FeatureRoute flag="qr_generator"><QRGenerator /></FeatureRoute>} />
      <Route path="/q/:code" element={<QRRedirect />} />
      <Route path="/my-qr-codes" element={<ProtectedRoute><MyQRCodes /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/studio" replace />} />
    </Routes>
  );
}

function GlobalChrome() {
  // Hide the ambient chat/feedback chips on the QR public surfaces
  const location = useLocation();
  const isPublicQrRoute = location.pathname.startsWith("/qr-generator") || location.pathname.startsWith("/q/");
  if (isPublicQrRoute) return null;
  return (
    <>
      <InsufficientCreditsModal />
      <FeedbackChip />
      <NovaChat />
      <DevPanel />
    </>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <DevModeProvider>
            <AppRouter />
            <GlobalChrome />
            <Toaster
              position="bottom-right"
              toastOptions={{
                style: {
                  background: "#120E15",
                  border: "1px solid rgba(242, 201, 76, 0.25)",
                  color: "#FFFFFF",
                  fontFamily: "Outfit, sans-serif",
                },
              }}
            />
          </DevModeProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
